import { playbackProgress } from "../playback-display";
import type { ParserMode, RevealMode } from "../types";

export function shouldContinueVoicePlayback(wasActive: boolean, linePlaybackEnabled: boolean): boolean {
  return wasActive && linePlaybackEnabled;
}

export function nextPlaybackPosition(current: number, direction: -1 | 1, lineCount: number, shouldSkip: (position: number) => boolean): number {
  let candidate = current + direction;
  while (candidate >= 0 && candidate < lineCount && shouldSkip(candidate)) candidate += direction;
  return candidate >= 0 && candidate < lineCount ? candidate : current;
}

export interface PlayerControlActions {
  toggleSmartReview(): void;
  scoreSmartReview(score: number): void;
  selectRole(characterId: string, selected: boolean): void;
  setSkipOtherLines(enabled: boolean): void;
  setRevealMode(mode: RevealMode): void;
  setLineVisibility(key: "hideMine" | "hideOthers" | "showOtherLinesNormally", enabled: boolean): void;
  adjustWordReveal(direction: -1 | 1): void;
  revealLine(): void;
  setVoiceReading(key: "readOthersLines" | "readMyLines" | "autoProceed", enabled: boolean): void;
  assignVoice(characterId: string, voiceId: string | undefined): void;
  setPlaybackSpeed(speed: number): void;
  setLanguage(language: string): void;
  setRecognitionThreshold(threshold: number): void;
  setIgnoredWords(value: string): void;
  setTags(value: string): void;
  setNotes(value: string): void;
  replaceSource(sourceText: string, parserMode: ParserMode): void;
  previousLine(): void;
  nextLine(): void;
  advanceFromLine(): void;
  speak(): void;
  listen(): void;
  goTo(position: number): void;
  export(format: string): void;
}

export function bindPlayerControls(root: HTMLElement, actions: PlayerControlActions, signal: AbortSignal): void {
  onClick(root, "[data-smart-review]", () => actions.toggleSmartReview(), signal);
  root.querySelectorAll<HTMLButtonElement>("[data-smart-score]").forEach((button) => on(button, "click", () => actions.scoreSmartReview(Number(button.dataset.smartScore)), signal));
  root.querySelectorAll<HTMLInputElement>("[data-role]").forEach((input) => on(input, "change", () => actions.selectRole(input.dataset.role ?? "", input.checked), signal));
  onCheckbox(root, "[data-skip]", actions.setSkipOtherLines, signal);
  root.querySelectorAll<HTMLInputElement>('input[name="reveal"]').forEach((input) => on(input, "change", () => actions.setRevealMode(input.value as RevealMode), signal));
  onCheckbox(root, "[data-hide-mine]", (enabled) => actions.setLineVisibility("hideMine", enabled), signal);
  onCheckbox(root, "[data-hide-others]", (enabled) => actions.setLineVisibility("hideOthers", enabled), signal);
  onCheckbox(root, "[data-show-others-normal]", (enabled) => actions.setLineVisibility("showOtherLinesNormally", enabled), signal);
  root.querySelectorAll<HTMLButtonElement>("[data-reveal-word]").forEach((button) => on(button, "click", () => actions.adjustWordReveal(button.dataset.revealWord === "back" ? -1 : 1), signal));
  onClick(root, "[data-show-line]", () => actions.revealLine(), signal);
  onCheckbox(root, "[data-read-others]", (enabled) => actions.setVoiceReading("readOthersLines", enabled), signal);
  onCheckbox(root, "[data-read-mine]", (enabled) => actions.setVoiceReading("readMyLines", enabled), signal);
  onCheckbox(root, "[data-auto-proceed]", (enabled) => actions.setVoiceReading("autoProceed", enabled), signal);
  root.querySelectorAll<HTMLSelectElement>("[data-voice]").forEach((select) => on(select, "change", () => actions.assignVoice(select.dataset.voice ?? "", select.value || undefined), signal));
  bindRange(root, "[data-speed]", (value) => `${value.toFixed(1)}×`, actions.setPlaybackSpeed, signal);
  bindTextInput(root, "[data-language]", actions.setLanguage, signal);
  bindRange(root, "[data-recognition-threshold]", (value) => `${Math.round(value * 100)}%`, actions.setRecognitionThreshold, signal);
  bindTextInput(root, "[data-ignored-words]", actions.setIgnoredWords, signal);
  bindTextInput(root, "[data-script-tags]", actions.setTags, signal);
  bindTextInput(root, "[data-script-notes]", actions.setNotes, signal);
  bindSourceEditor(root, actions.replaceSource, signal);
  onClick(root, "[data-prev]", actions.previousLine, signal);
  onClick(root, "[data-next]", actions.nextLine, signal);
  onClick(root, "[data-next-line]", actions.advanceFromLine, signal);
  onClick(root, "[data-speak]", actions.speak, signal);
  onClick(root, "[data-mic]", actions.listen, signal);
  bindPosition(root, actions.goTo, signal);
  bindDialogs(root, actions, signal);
}

function bindSourceEditor(root: HTMLElement, replaceSource: (sourceText: string, parserMode: ParserMode) => void, signal: AbortSignal): void {
  const dialog = root.querySelector<HTMLDialogElement>(".source-dialog");
  const source = root.querySelector<HTMLTextAreaElement>("[data-source-fix]");
  const parser = root.querySelector<HTMLSelectElement>("[data-source-parser]");
  onClick(root, "[data-edit-source]", () => dialog?.showModal(), signal);
  onClick(root, "[data-close-source]", () => dialog?.close(), signal);
  onClick(root, "[data-cancel-source]", () => dialog?.close(), signal);
  onClick(root, "[data-save-source]", () => {
    if (!source?.value.trim() || !parser) {
      source?.focus();
      return;
    }
    replaceSource(source.value, parser.value as ParserMode);
  }, signal);
}

function bindPosition(root: HTMLElement, goTo: (position: number) => void, signal: AbortSignal): void {
  const position = root.querySelector<HTMLInputElement>("[data-position]");
  if (!position) return;
  const labels = root.querySelectorAll<HTMLElement>(".progress > div > span");
  on(position, "input", () => {
    // Keep the native range control mounted while dragging. Rebuilding the
    // player here interrupts pointer capture and makes the thumb judder.
    const preview = playbackProgress(Number(position.value), Number(position.max) + 1);
    if (labels[0]) labels[0].textContent = preview.lineLabel;
    if (labels[1]) labels[1].textContent = preview.percentLabel;
  }, signal);
  on(position, "change", () => goTo(Number(position.value)), signal);
}

function bindDialogs(root: HTMLElement, actions: PlayerControlActions, signal: AbortSignal): void {
  const exportDialog = root.querySelector<HTMLDialogElement>(".export-dialog");
  const jumpDialog = root.querySelector<HTMLDialogElement>(".jump-dialog");
  onClick(root, "[data-export]", () => exportDialog?.showModal(), signal);
  onClick(root, "[data-close-dialog]", () => exportDialog?.close(), signal);
  onClick(root, "[data-jump]", () => jumpDialog?.showModal(), signal);
  onClick(root, "[data-close-jump]", () => jumpDialog?.close(), signal);
  root.querySelectorAll<HTMLButtonElement>("[data-jump-position]").forEach((button) => on(button, "click", () => {
    jumpDialog?.close();
    actions.goTo(Number(button.dataset.jumpPosition));
  }, signal));
  bindJumpSearch(root, signal);
  root.querySelectorAll<HTMLButtonElement>("[data-format]").forEach((button) => on(button, "click", () => actions.export(button.dataset.format ?? "txt"), signal));
}

function bindJumpSearch(root: HTMLElement, signal: AbortSignal): void {
  const search = root.querySelector<HTMLInputElement>("[data-jump-search]");
  if (!search) return;
  on(search, "input", () => {
    const query = search.value.toLowerCase();
    root.querySelectorAll<HTMLElement>("[data-jump-item]").forEach((item) => { item.hidden = !item.dataset.jumpItem?.includes(query); });
  }, signal);
}

function bindRange(root: HTMLElement, selector: string, format: (value: number) => string, update: (value: number) => void, signal: AbortSignal): void {
  const input = root.querySelector<HTMLInputElement>(selector);
  if (!input) return;
  on(input, "input", () => {
    const value = Number(input.value);
    update(value);
    if (input.nextElementSibling) input.nextElementSibling.textContent = format(value);
  }, signal);
}

function bindTextInput(root: HTMLElement, selector: string, update: (value: string) => void, signal: AbortSignal): void {
  const input = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);
  if (input) on(input, "change", () => update(input.value), signal);
}

function onCheckbox(root: HTMLElement, selector: string, update: (checked: boolean) => void, signal: AbortSignal): void {
  const input = root.querySelector<HTMLInputElement>(selector);
  if (input) on(input, "change", () => update(input.checked), signal);
}

function onClick(root: HTMLElement, selector: string, callback: (event: MouseEvent) => void, signal: AbortSignal): void {
  const element = root.querySelector<HTMLElement>(selector);
  if (element) on(element, "click", callback, signal);
}

function on<K extends keyof HTMLElementEventMap>(element: HTMLElement, type: K, callback: (event: HTMLElementEventMap[K]) => void, signal: AbortSignal): void {
  element.addEventListener(type, callback as EventListener, { signal });
}
